import { useEffect, useState, useSyncExternalStore } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from 'api';
import { chooseProfile, onActiveProfileChange, readActiveProfile, writeActiveProfile } from 'utils/activeProfile';

const NO_SUBSCRIPTION = { hasSubscription: false, needsProfile: false, token: null };

// Module-level so the nav and the menu share one lookup, and a layout remount reuses it.
let shared = null; // { profileKey, promise, result }

const activeProfileKey = () => readActiveProfile()?.token || '';

function loadStatus(profileKey) {
  if (shared?.profileKey === profileKey) return shared;
  const entry = { profileKey, result: null };
  entry.promise = (async () => {
    try {
      const { data: auth } = await api.get('/auth/token', { timeout: 15000 });
      const chosen = chooseProfile(auth);
      // Several profiles and none picked: use the only one that has a subscription.
      const candidates = chosen ? [chosen] : (auth.profiles || []);
      const found = (await Promise.all(candidates.map(async (profile) => {
        try {
          const { data } = await api.get(`/orders/${profile.token}/subscriptions`, { timeout: 25000 });
          return data?.subscriptions?.length ? profile : null;
        } catch {
          return null;
        }
      }))).filter(Boolean);
      const match = found.length === 1 ? found[0] : null;
      entry.result = {
        hasSubscription: Boolean(match),
        needsProfile: !chosen && found.length > 1,
        token: match ? match.token : null,
      };
      if (match && !chosen) {
        // Re-key the entry so the profile change below reuses this result.
        entry.profileKey = match.token;
        writeActiveProfile(match);
      }
    } catch {
      entry.result = NO_SUBSCRIPTION;
      // Don't keep a failure for the session; the next mount retries.
      if (shared === entry) shared = null;
    }
    return entry.result;
  })();
  shared = entry;
  return entry;
}

/**
 * Whether the signed-in customer already has an ACTIVE subscription, so the
 * à la carte menu can send them to their meal plan instead. Guests and any
 * lookup failure resolve to `hasSubscription: false` — the menu stays usable.
 * Several subscribed profiles and none picked sets `needsProfile` so the page can ask which one.
 * `checking` is true only while a signed-in customer's status is being fetched.
 */
export function useSubscriptionGate() {
  const { isAuthenticated, isLoading } = useAuth0();
  const profileKey = useSyncExternalStore(onActiveProfileChange, activeProfileKey);
  const [state, setState] = useState(() => ({ checking: false, ...(shared?.result || NO_SUBSCRIPTION) }));

  useEffect(() => {
    if (isLoading) {
      setState((s) => ({ ...s, checking: true }));
      return undefined;
    }
    if (!isAuthenticated) {
      shared = null;
      setState({ checking: false, ...NO_SUBSCRIPTION });
      return undefined;
    }

    const entry = loadStatus(profileKey);
    if (entry.result) {
      setState({ checking: false, ...entry.result });
      return undefined;
    }
    let cancelled = false;
    setState((s) => ({ ...s, checking: true }));
    entry.promise.then((result) => {
      if (!cancelled) setState({ checking: false, ...result });
    });
    return () => { cancelled = true; };
  }, [isAuthenticated, isLoading, profileKey]);

  return state;
}
